import React, { useState, useEffect } from "react";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { settingsValidation } from "../../utils/validationSchema/settingsValidation";

export const useSettings = () => {
  const [formData, setFormData] = useState({
    title: "",
    logo: null,
    shortDescription: "",
    longDescription: "",
    bppId: "",
    bppUrl: "",
    locality: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    storeEmail: "",
    storeContactNo: "",
    authorizedPersonName: "",
    dateOfIncorporation: "",
    panNumber: "",
    nameAsPerPAN: "",
    gstNumber: "",
    beneficiaryName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
    branch: "",
  });

  const [errors, setErrors] = useState({});
  const { addToast } = useToasts();

  const [loader, setLoader] = useState(false);
  const { authData } = useSelector((state) => state.loginSlice);
  const dispatch = useDispatch();

  const fetchSettings = async () => {
    try {
      setLoader(true);
      const url = `${endpoint.GET_SETTINGS}`;

      const response = await apiHandler({
        url: url,
        method: "GET",
        authToken: authData,
        dispatch,
      });

      if (response.data.success && response.data.data.length > 0) {
        const fetchedSettings = response.data.data[0];

        setFormData({
          title: fetchedSettings.bpp_name || "",
          logo: fetchedSettings.logo || null,
          shortDescription: fetchedSettings.bpp_short_desc || "",
          longDescription: fetchedSettings.bpp_long_desc || "",
          bppId: fetchedSettings.bpp_id || "",
          bppUrl: fetchedSettings.bpp_url || "",
          locality: fetchedSettings.store_address?.locality || "",
          street: fetchedSettings.store_address?.street || "",
          city: fetchedSettings.store_address?.city || "",
          state: fetchedSettings.store_address?.state || "",
          pincode: fetchedSettings.store_address?.pincode || "",
          country: fetchedSettings.store_address?.country || "",
          storeEmail: fetchedSettings.store_email || "",
          storeContactNo: fetchedSettings.store_phone_number || "",
          authorizedPersonName: fetchedSettings.authorised_signatory || "",
          dateOfIncorporation: fetchedSettings.date_of_incorporation
            ? fetchedSettings.date_of_incorporation.split("T")[0]
            : "",

          panNumber: fetchedSettings.store_pan_number || "",
          nameAsPerPAN: fetchedSettings.name_as_per_pan || "",
          gstNumber: fetchedSettings.store_gst_number || "",
          beneficiaryName:
            fetchedSettings.store_bank_details?.account_holder_name || "",
          accountNumber:
            fetchedSettings.store_bank_details?.account_number || "",
          ifscCode: fetchedSettings.store_bank_details?.ifsc_code || "",
          bankName: fetchedSettings.store_bank_details?.bank_name || "",
          branch: fetchedSettings.store_bank_details?.branch || "",
        });
      }
    } catch (error) {

    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    validateField(name, type === "file" ? files[0] : value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    validateField(name, value);
  };

  const validateField = (name, value) => {
    if (value === undefined || value === null) {
      value = "";
    } else {
      value = value.toString().trim();
    }

    const fieldSchema = settingsValidation.extract(name);
    const { error } = fieldSchema.validate(value);

    if (error) {

    }

    let fieldError = null;
    if (error) {
      const message = error.details[0].message;
      const label = formData[name] || name;

      // Replace field name (in lowercase) in the message with human-readable label
      fieldError = message.replace(/^[^a-zA-Z]*/, "").replace(name, label);
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: fieldError,
    }));
  };

  const handleSubmit = async () => {
    const { error } = settingsValidation.validate(formData, {
      abortEarly: false,
    });

    if (error) {
      const errorObject = error.details.reduce((acc, curr) => {
        const field = curr.path[0];
        acc[field] = curr.message;
        return acc;
      }, {});
      setErrors(errorObject);
      return;
    }

    try {
      setLoader(true);

      const formPayload = new FormData();

      formPayload.append("bpp_name", formData.title);
      formPayload.append("bpp_id", formData.bppId);
      formPayload.append("bpp_url", formData.bppUrl);
      formPayload.append("bpp_long_desc", formData.longDescription);
      formPayload.append("bpp_short_desc", formData.shortDescription);
      if (formData.logo && typeof formData.logo !== "string") {
        formPayload.append("logo", formData.logo);
      }

      formPayload.append("store_email", formData.storeEmail);
      formPayload.append("store_phone_number", formData.storeContactNo);
      formPayload.append("store_pan_number", formData.panNumber);
      formPayload.append("store_gst_number", formData.gstNumber);
      formPayload.append("name_as_per_pan", formData.nameAsPerPAN);
      formPayload.append("date_of_incorporation", formData.dateOfIncorporation);
      formPayload.append("authorised_signatory", formData.authorizedPersonName);

      // Address fields
      formPayload.append("store_address[street]", formData.street);
      formPayload.append("store_address[locality]", formData.locality);
      formPayload.append("store_address[city]", formData.city);
      formPayload.append("store_address[state]", formData.state);
      formPayload.append("store_address[pincode]", formData.pincode);
      formPayload.append("store_address[country]", formData.country);

      // Bank fields
      formPayload.append(
        "store_bank_details[account_holder_name]",
        formData.beneficiaryName
      );
      formPayload.append(
        "store_bank_details[account_number]",
        formData.accountNumber
      );
      formPayload.append("store_bank_details[ifsc_code]", formData.ifscCode);
      formPayload.append("store_bank_details[bank_name]", formData.bankName);
      formPayload.append("store_bank_details[branch]", formData.branch);

      const response = await apiHandler({
        url: `${endpoint.UPDATE_SETTINGS}`,
        method: "POST",
        data: formPayload,
        authToken: authData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        dispatch,
      });

      if (response.data.success) {
        addToast(response.data.message, {
          appearance: "success",
          autoDismiss: true,
        });
        fetchSettings();
      } else {
        addToast("Error updating settings", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } catch (error) {
      addToast("Error updating settings", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = () => {
  fetchSettings(); // Re-fetch or reset form data to last saved state
  setErrors({});   // Clear any validation errors
};

  return {
    formData,
    errors, // return errors state
    handleChange,
    handleBlur,
    handleSubmit,
    loader,
    handleCancel
  };
};
